import { getBestDecisions, type BestDecisionsResult } from "@beyourbest/decision-engine";
import { BallState, Player } from "@Containers/game/builder/models/builder-data-types";
import { useBuilderContext } from "@Context/game/builder/builder-context";
import {
  appendScoreAction,
  buildFallbackSamples,
  buildTimelineSegments,
  clearExistingScoreActions,
  clonePlayersWithActions,
  getDecisionItems,
  getDecisionWindow,
  getDefaultZeroScoreSegments,
  removeScoreActionsInWindow,
  resolvePlayerMapKey,
  toInternalPlayerId,
} from "@hooks/game/builder/builder-decision-helpers";
import { buildReplayDataFromScenarioClip, findPossessorAtTime } from "@utilities/scenario-builder-replay";
import useI18n from "@i18n";
import { clamp } from "lodash-es";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseBuilderDecisionsParams {
  isPlaying: boolean;
  enabled: boolean;
  pausePlayback: () => void;
}

interface UseBuilderDecisionsReturn {
  decisionSnapshot: BestDecisionsResult | null;
  autoAssignDecisionScores: () => Promise<AutoAssignDecisionResult | undefined>;
  autoAssignLoading: boolean;
}

type AutoAssignDecisionResult = {
  status: "success" | "error";
  message: string;
};

const MAX_TOP_DECISIONS_COUNT = 11;

const useBuilderDecisions = ({ isPlaying, enabled, pausePlayback }: UseBuilderDecisionsParams): UseBuilderDecisionsReturn => {
  const { i18n } = useI18n();
  const { state, dispatch } = useBuilderContext();
  const { clip, field, ballStates, options, time } = state;
  const playbackResolution = options.playbackResolution;
  const snapshotRequestIdRef = useRef(0);
  const [decisionSnapshot, setDecisionSnapshot] = useState<BestDecisionsResult | null>(null);
  const [autoAssignLoading, setAutoAssignLoading] = useState(false);

  const loadDecisionSnapshot = useCallback(
    async (requestId: number) => {
      const scenario = clip?.scenario;
      if (!scenario || scenario.endTime <= 0 || scenario.players.size === 0) {
        setDecisionSnapshot(null);
        return;
      }

      try {
        const replayData = buildReplayDataFromScenarioClip({
          clip,
          field,
          ballStates: ballStates as BallState[],
          sampleStep: playbackResolution,
        });

        const currentPossessorId = findPossessorAtTime({
          ballStates: ballStates as BallState[],
          ballPoints: scenario.ball.points,
          time,
          initialPossessor: scenario.ball.initialPossessor,
        });

        replayData.characters = replayData.characters.map(character => ({
          ...character,
          isPlayer: character.id === String(currentPossessorId ?? ""),
        }));

        const result = await getBestDecisions(replayData as Parameters<typeof getBestDecisions>[0], { evalTime: time, topDecisionsCount: 8 });

        if (snapshotRequestIdRef.current !== requestId) {
          return;
        }

        if (!result.success || !result.data?.length) {
          setDecisionSnapshot(null);
          return;
        }

        setDecisionSnapshot(result.data[0] ?? null);
      } catch (error) {
        console.error("[Scenario Builder] snapshot decision replay failed", error);
        if (snapshotRequestIdRef.current === requestId) {
          setDecisionSnapshot(null);
        }
      }
    },
    [ballStates, clip, field, playbackResolution, time]
  );

  const autoAssignDecisionScores = useCallback(async () => {
    if (autoAssignLoading) return;

    pausePlayback();

    const scenario = clip?.scenario;
    if (!scenario || scenario.endTime <= 0 || scenario.players.size === 0) {
      return {
        status: "error",
          message: i18n("builder.decision.autoassign.error.noScenario"),
      } as AutoAssignDecisionResult;
    }

    const hasUserPlayer = Array.from(scenario.players.values()).some((player: Player) => player.isPlayer);
    if (!hasUserPlayer) {
      return {
        status: "error",
        message: i18n("builder.decision.autoassign.error.noUserPlayer"),
      } as AutoAssignDecisionResult;
    }

    setAutoAssignLoading(true);

    try {
      const replayData = buildReplayDataFromScenarioClip({
        clip,
        field,
        ballStates: ballStates as BallState[],
        sampleStep: playbackResolution,
      });

      const result = await getBestDecisions(replayData as Parameters<typeof getBestDecisions>[0], {
        topDecisionsCount: MAX_TOP_DECISIONS_COUNT,
      });

      if (!result.success || !result.data || result.data.length === 0) {
        return {
          status: "error",
          message: i18n("builder.decision.autoassign.error.noTimelineData"),
        } as AutoAssignDecisionResult;
      }

      const moments = result.data.map(moment => ({
        ...moment,
        decisions: {
          ...moment.decisions,
          user: moment.decisions.user ?? null,
        },
      }));

      const updatedPlayers = clonePlayersWithActions(scenario.players);
      const { removedCount } = clearExistingScoreActions(updatedPlayers);
      const touchedPlayerIds = new Set<number>();
      const defaultedPlayerNames = new Set<string>();
      let created = 0;
      let totalOptions = 0;
      let didMutateScores = removedCount > 0;

      const findAssigneePlayerId = (targetPlayerId: number | string | undefined, evalTime: number, allowPossessorFallback: boolean = true) => {
        const receiverId = toInternalPlayerId(targetPlayerId);
        if (receiverId !== null) {
          return resolvePlayerMapKey(updatedPlayers, receiverId);
        }

        if (!allowPossessorFallback) {
          return null;
        }

        const possessorId = findPossessorAtTime({
          ballStates: ballStates as BallState[],
          ballPoints: scenario.ball.points,
          time: evalTime,
          initialPossessor: scenario.ball.initialPossessor,
        });

        return resolvePlayerMapKey(updatedPlayers, possessorId);
      };

      for (const moment of moments) {
        const decisionItems = getDecisionItems(moment);
        if (decisionItems.length === 0) continue;

        totalOptions += decisionItems.length;

        const { momentStart, windowStart, windowEnd } = getDecisionWindow({
          moment,
          playbackResolution,
          scenarioEndTime: scenario.endTime,
          decisionItems,
        });

        const assignedPlayersForMoment = new Set<number>();
        let bestDecisionForMoment: {
          segments: Array<{ startTime: number; endTime: number; score: number }>;
          isShotDecision: boolean;
          topScore: number;
        } | null = null;
        let bestShotDecisionForMoment: {
          segments: Array<{ startTime: number; endTime: number; score: number }>;
          isShotDecision: boolean;
          topScore: number;
        } | null = null;

        for (const decisionItem of decisionItems) {
          const fallbackEvalTime = decisionItem.samples?.find(sample => Number.isFinite(sample.evalTime))?.evalTime ?? windowStart;
          const timelineEvalTime = clamp(decisionItem.evalTime ?? fallbackEvalTime, windowStart, windowEnd);
          const isShotDecision = (decisionItem.passType ?? "").toLowerCase() === "shot";

          const fallbackSamples = buildFallbackSamples(decisionItem, timelineEvalTime);
          const segments = buildTimelineSegments(fallbackSamples, windowStart, windowEnd);
          if (segments.length === 0) continue;

          const topScore = Math.max(...segments.map(segment => segment.score));
          if (!bestDecisionForMoment || topScore > bestDecisionForMoment.topScore) {
            bestDecisionForMoment = { segments, isShotDecision, topScore };
          }
          if (isShotDecision && (!bestShotDecisionForMoment || topScore > bestShotDecisionForMoment.topScore)) {
            bestShotDecisionForMoment = { segments, isShotDecision: true, topScore };
          }

          const playerId = findAssigneePlayerId(decisionItem.targetPlayerId, timelineEvalTime, true);
          if (playerId === null || assignedPlayersForMoment.has(playerId)) {
            continue;
          }

          const player = updatedPlayers.get(playerId);
          if (!player) continue;

          assignedPlayersForMoment.add(playerId);
          removeScoreActionsInWindow(player, windowStart, windowEnd);
          segments.forEach(segment => {
            if (appendScoreAction({ player, ...segment, isShotDecision })) {
              created += 1;
              touchedPlayerIds.add(playerId);
            }
          });
          didMutateScores = true;
        }

        const possessorEvalTime = clamp(moment.decisions?.top?.[0]?.evalTime ?? momentStart, windowStart, windowEnd);
        const possessorId = findAssigneePlayerId(undefined, possessorEvalTime, true);
        const possessorDecisionForMoment = bestShotDecisionForMoment ?? bestDecisionForMoment;
        if (possessorDecisionForMoment && possessorId !== null && !assignedPlayersForMoment.has(possessorId)) {
          const possessor = updatedPlayers.get(possessorId);
          if (possessor) {
            removeScoreActionsInWindow(possessor, windowStart, windowEnd);
            possessorDecisionForMoment.segments.forEach(segment => {
              if (appendScoreAction({ player: possessor, ...segment, isShotDecision: possessorDecisionForMoment.isShotDecision })) {
                created += 1;
                touchedPlayerIds.add(possessorId);
              }
            });
            didMutateScores = true;
          }
        }

        const possessionTeam = possessorId !== null ? updatedPlayers.get(possessorId)?.team : undefined;
        if (possessionTeam !== undefined) {
          const defaultZeroSegments = getDefaultZeroScoreSegments(windowStart, windowEnd);
          if (defaultZeroSegments.length > 0) {
            updatedPlayers.forEach((candidatePlayer, candidatePlayerId) => {
              if (candidatePlayer.team !== possessionTeam || assignedPlayersForMoment.has(candidatePlayerId)) {
                return;
              }

              removeScoreActionsInWindow(candidatePlayer, windowStart, windowEnd);
              defaultZeroSegments.forEach(segment => {
                if (appendScoreAction({ player: candidatePlayer, ...segment, isShotDecision: false })) {
                  created += 1;
                  touchedPlayerIds.add(candidatePlayerId);
                  defaultedPlayerNames.add(candidatePlayer.name);
                }
              });
              didMutateScores = true;
            });
          }
        }
      }

      if (!didMutateScores && created === 0) {
        return {
          status: "error",
          message: i18n("builder.decision.autoassign.error.noScoreWindows"),
        } as AutoAssignDecisionResult;
      }

      if (totalOptions === 0) {
        return {
          status: "error",
          message: i18n("builder.decision.autoassign.error.noScoreWindows"),
        } as AutoAssignDecisionResult;
      }

      if (didMutateScores || created > 0) {
        dispatch({
          clip: {
            ...clip,
            scenario: {
              ...scenario,
              players: updatedPlayers,
            },
          },
        });
      }

      const visibleDefaultedPlayers = Array.from(defaultedPlayerNames)
        .sort((a, b) => a.localeCompare(b))
        .slice(0, 3);
      const remainingDefaultedPlayersCount = Math.max(0, defaultedPlayerNames.size - visibleDefaultedPlayers.length);
      const replacedText = removedCount > 0 ? i18n("builder.decision.autoassign.success.replaced", { count: String(removedCount) }) : "";
      const defaultedText =
        visibleDefaultedPlayers.length > 0
          ? i18n("builder.decision.autoassign.success.defaulted", {
              players: visibleDefaultedPlayers.join(", "),
              more: remainingDefaultedPlayersCount > 0 ? i18n("builder.decision.autoassign.success.defaulted.more", { count: String(remainingDefaultedPlayersCount) }) : "",
            })
          : "";

      return {
        status: "success",
        message: i18n("builder.decision.autoassign.success", {
          created: String(created),
          players: String(touchedPlayerIds.size),
          replacedText,
          defaultedText,
        }),
      } as AutoAssignDecisionResult;
    } catch (error) {
      console.error("[Scenario Builder] auto-assign failed", error);
      return {
        status: "error",
        message: i18n("builder.decision.autoassign.error.generic"),
      } as AutoAssignDecisionResult;
    } finally {
      setAutoAssignLoading(false);
    }
  }, [autoAssignLoading, ballStates, clip, dispatch, field, pausePlayback, playbackResolution]);

  useEffect(() => {
    const scenario = clip?.scenario;
    const hasValidScenario = !!scenario && scenario.endTime > 0 && scenario.players.size > 0;

    snapshotRequestIdRef.current += 1;
    const requestId = snapshotRequestIdRef.current;

    if (!hasValidScenario) {
      setDecisionSnapshot(null);
      return;
    }

    if (!enabled) {
      setDecisionSnapshot(null);
      return;
    }

    if (isPlaying) {
      return;
    }

    void loadDecisionSnapshot(requestId);
  }, [clip, enabled, isPlaying, loadDecisionSnapshot, time]);

  return {
    decisionSnapshot,
    autoAssignDecisionScores,
    autoAssignLoading,
  };
};

export default useBuilderDecisions;
